import {
  Autocomplete,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from '@mui/material';
import { isEmpty } from 'lodash';
import React, { useMemo } from 'react';
import FormFieldWrapper from './FormFieldWrapper';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';
import { MAIN_THEME } from '@styles';
import { IFormFieldInputBaseProps } from '.';
import { Clear } from '@mui/icons-material';
import { useAppSelector } from '@redux/hooks';
import { GlobalLovInjector, ILovsReducer } from '@redux/lovs/types';
import { IAbstractRecord } from '@models';

interface ISelectFormFieldBase extends IFormFieldInputBaseProps {
  value: string;
  canClearValue?: boolean;
  loader?: boolean;
  freeSolo?: boolean;
  showTooltipOnDisabled?: boolean;
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
  onFocus?: () => void;
  classes?: { input?: string; label?: string; wrapper?: string };
}

// 1) If `injectGlobalLov` is not provided, `selectOptions` is required.
interface ISelectFormFieldWithLocalOptions extends ISelectFormFieldBase {
  selectOptions: Record<string, string>;
  injectGlobalLov?: never;
}

// 2) If `injectGlobalLov` is provided, `selectOptions` is not allowed.
interface ISelectFormFieldWithGlobalLov extends ISelectFormFieldBase {
  injectGlobalLov: GlobalLovInjector;
  selectOptions?: never;
}

export type ISelectFormFieldProps =
  | ISelectFormFieldWithLocalOptions
  | ISelectFormFieldWithGlobalLov;

const useStyles = makeStyles<{
  error?: string;
}>()((theme, { error }) => ({
  inputContainerStyles: {
    width: '100%',
  },
  inputWrapper: {
    position: 'relative',
  },
  inputStyles: {
    width: '100%',
    border: `1px solid ${MAIN_THEME.color.white86}`,
    borderRadius: '5px',
    outline: 'none',
    // maxHeight: '34px',
    '& .MuiInputBase-input': {
      fontFamily: MAIN_THEME.font.regular,
      '&::placeholder': {
        fontStyle: 'italic',
        fontFamily: MAIN_THEME.font.regular,
        color: MAIN_THEME.color.white86,
        opacity: 1,
        fontSize: MAIN_THEME.fontSize.sm,
      },
    },
    '& .Mui-disabled': {
      cursor: 'not-allowed',
    },
    '& .MuiInputBase-root': {
      fontSize: MAIN_THEME.fontSize.sm,
      lineHeight: '15px',
      minHeight: '33.4px',
      width: '100%',
      padding: '0 25px 0 15px!important',
      margin: '0 auto',
      backgroundColor: MAIN_THEME.color.white98,
      outlineColor: error ? theme.palette.error.main : undefined,
      border: error
        ? `1px solid ${theme.palette.error.main}`
        : `0px solid ${MAIN_THEME.color.white92}`,
      boxSizing: 'border-box',
      borderRadius: '5px',
      '&::before': {
        display: 'none',
      },
      '&::after': {
        display: 'none',
      },
    },
  },
  optionDesign: {
    fontSize: `${MAIN_THEME.fontSize.sm}`,
    lineHeight: '15px',
    color: MAIN_THEME.color.black,
    fontFamily: MAIN_THEME.font.regular,
  },
  inputErrorStyles: {
    minHeight: '25px',
    color: MAIN_THEME.color.red,
    textAlign: 'left',
    fontSize: MAIN_THEME.fontSize.xs,
    marginTop: '5px',
  },
  inputRedBorder: {
    border: `1px solid ${MAIN_THEME.color.red}`,
  },
  chipStyle: {
    height: '25px',
  },
  loader: {
    position: 'absolute',
    top: 'calc(50% - 10px)',
    right: '6%',
    opacity: 1,
    height: '25px',
    width: '25px',
  },
  selectMaterial: {
    width: '100%',
    '& .MuiInput-input': {
      fontFamily: MAIN_THEME.font.regular,
      fontSize: `${MAIN_THEME.fontSize.sm}`,
      lineHeight: '15px',
      color: `${MAIN_THEME.color.black}`,
      paddingBottom: '0px !important',
      '& .Mui-disabled': {
        cursor: 'not-allowed',
      },
      '&::placeholder': {
        color: 'rgba(0, 0, 0, 0.20)',
        fontStyle: 'italic',
        opacity: '10',
        fontSize: `${MAIN_THEME.fontSize.sm} !important`,
      },
    },
    '.Mui-disabled': {
      cursor: 'not-allowed',
    },
    '& .MuiInputBase-root:before': {
      border: 'none',
      backgroundColor: 'unset !important',
      borderRadius: '0 !important',
      borderBottom: `1px solid ${MAIN_THEME.color.white86}`,
    },
    '& .MuiInput-root': {
      height: '34px',
      paddingBottom: '0px !important',
    },
    '& .MuiInput-root.Mui-disabled:before': {
      borderBottomStyle: 'solid',
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before, & .MuiInputBase-root:after':
      {
        borderBottom: `1px solid ${MAIN_THEME.color.white86}`,
      },
  },
  selectError: {
    '& .MuiInputBase-root:before': {
      borderBottom: `1px solid ${theme.palette.error.main}`,
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before, & .MuiInputBase-root:after':
      {
        borderBottom: `1px solid ${theme.palette.error.main}`,
      },
    '& .MuiInputBase-root:focus': {
      borderColor: theme.palette.error.main,
      outlineColor: theme.palette.error.main,
      boxShadow: 'none',
      borderRadius: 0,
    },
    '& .MuiInputBase-root': {
      borderColor: `${theme.palette.error.main}`,
      outlineColor: `${theme.palette.error.main}`,
    },
  },
  clearBtnContainer: {
    marginRight: '10px',
  },
  clearBtn: {
    cursor: 'pointer',
    height: '12px',
    width: '12px',
  },
}));

const SelectFormField: React.FC<ISelectFormFieldProps> = ({
  name,
  title,
  placeholder,
  className,
  style,
  classes,
  value,
  error,
  hideError,
  onChange,
  onBlur = () => {},
  onFocus = () => {},
  disabled,
  selectOptions = {},
  loader,
  inputOnly,
  fullWidth,
  freeSolo = false,
  material,
  canClearValue = false,
  showTooltipOnDisabled,
  injectGlobalLov,
}) => {
  const { classes: inputClasses } = useStyles({ error });
  const lovs = useAppSelector((state) => state.lovs);

  const handleClearValue = () => {
    onChange?.('');
  };

  const localSelectOptions: IAbstractRecord = useMemo(() => {
    if (injectGlobalLov) {
      const list: IAbstractRecord = {};
      const globalLov = lovs[injectGlobalLov.type];
      globalLov?.forEach((item) => {
        if (injectGlobalLov.display === 'Both') {
          list[item[injectGlobalLov.value]] = `${item.Symbol} - ${item.Title}`;
        } else {
          list[item[injectGlobalLov.value]] = item[injectGlobalLov.display];
        }
      });
      return list;
    }
    return selectOptions;
  }, [injectGlobalLov, lovs, selectOptions]);

  const AutoCompleteComponent = (
    <Autocomplete
      freeSolo={freeSolo}
      multiple={false}
      disabled={!!disabled}
      disablePortal={false}
      options={Object.keys(localSelectOptions)}
      onChange={(event, newValue) => {
        onChange?.(newValue);
      }}
      onFocus={() => onFocus()}
      onBlur={() => onBlur(value)}
      value={value}
      getOptionLabel={(option) => localSelectOptions?.[option] || ''}
      disableClearable={true}
      selectOnFocus
      renderOption={(props, option) => (
        <li {...props} key={option}>
          <span className={inputClasses.optionDesign}>
            {localSelectOptions[option]}
          </span>
        </li>
      )}
      renderTags={(value, getTagProps) =>
        Array.isArray(value) &&
        (value as readonly string[]).map((option: string, index: number) =>
          !isEmpty(localSelectOptions[option]) ? (
            <Chip
              variant="filled"
              label={localSelectOptions[option]}
              style={{
                height: 'unset',
                color: MAIN_THEME.color.white,
                backgroundColor: MAIN_THEME.color.black25,
                borderRadius: '0px',
              }}
              {...getTagProps({ index })}
              key={index}
            />
          ) : (
            <Chip
              variant="filled"
              style={{
                height: 'unset',
                color: MAIN_THEME.color.white,
                backgroundColor: MAIN_THEME.color.black25,
                borderRadius: '0px',
              }}
              label={option}
              {...getTagProps({ index })}
              key={index}
            />
          )
        )
      }
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          placeholder={placeholder}
          className={clsx(
            classes?.input,
            !material && inputClasses.inputStyles,
            material && inputClasses.selectMaterial,
            error && inputClasses.selectError
          )}
          InputProps={{
            ...params.InputProps,
            startAdornment: params.InputProps.startAdornment,
            endAdornment: (
              <>
                {value && canClearValue && (
                  <InputAdornment
                    position="end"
                    className={inputClasses.clearBtnContainer}
                  >
                    <IconButton
                      aria-label="clear date"
                      onClick={handleClearValue}
                      edge="end"
                      size="small"
                      style={{ marginRight: material ? '30px' : '-17px' }}
                    >
                      <Clear className={inputClasses.clearBtn} />
                    </IconButton>
                  </InputAdornment>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          variant="standard"
        />
      )}
    />
  );

  const renderInput = () => {
    return (
      <FormControl
        error={!!error}
        disabled={!!disabled}
        className={inputClasses.inputContainerStyles}
        size="small"
        variant="outlined"
      >
        {showTooltipOnDisabled && disabled ? (
          <Tooltip title={localSelectOptions?.[value]}>
            {AutoCompleteComponent}
          </Tooltip>
        ) : (
          AutoCompleteComponent
        )}
      </FormControl>
    );
  };

  return inputOnly ? (
    renderInput()
  ) : (
    <>
      <FormFieldWrapper
        title={title}
        error={error}
        name={name}
        hideError={hideError}
        material={material}
        fullWidth={fullWidth}
        className={clsx(classes?.wrapper, className)}
        style={style}
      >
        {renderInput()}
        {loader && (
          <CircularProgress
            className={inputClasses.loader}
            size={20}
            thickness={3}
          />
        )}
      </FormFieldWrapper>
    </>
  );
};

export default SelectFormField;
